import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { getAllAircraft } from '../services/api';
import securityIcon from '../assets/icons/security.svg';
import topLogo from '../assets/toplogo.svg';
import './LoginPage.css';

const LoginPage = () => {
  const { login, user } = useAuth();
  const navigate = useNavigate();

  const [aircraft, setAircraft] = useState([]);
  const [form, setForm] = useState({ email: '', aircraft_id: '', personal_id: '' });
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [loginError, setLoginError] = useState('');
  const [acLoading, setAcLoading] = useState(true);
  const [acDropOpen, setAcDropOpen] = useState(false);
  const [acSearch, setAcSearch] = useState('');
  const acDropRef = useRef(null);
  const acSearchRef = useRef(null);

  useEffect(() => {
    if (user) navigate('/dashboard');
  }, [user, navigate]);

  useEffect(() => {
    getAllAircraft()
      .then(res => setAircraft(res.data.aircraft || []))
      .catch(() => setAircraft([]))
      .finally(() => setAcLoading(false));
  }, []);

  useEffect(() => {
    const handler = e => {
      if (acDropRef.current && !acDropRef.current.contains(e.target)) setAcDropOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  useEffect(() => {
    if (acDropOpen) acSearchRef.current?.focus();
  }, [acDropOpen]);

  const validate = () => {
    const e = {};
    if (!form.email.trim()) e.email = 'Email is required';
    if (!form.aircraft_id) e.aircraft_id = 'Please select an aircraft';
    if (!form.personal_id.trim()) e.personal_id = 'Personal ID (Password)';
    return e;
  };

  const handleChange = e => {
    const { name, value } = e.target;
    setForm(f => ({ ...f, [name]: value }));
    setErrors(er => ({ ...er, [name]: '' }));
    setLoginError('');
  };

  const handleSubmit = async e => {
    e.preventDefault();
    const errs = validate();
    if (Object.keys(errs).length) {
      setErrors(errs);
      return;
    }

    setLoading(true);
    const result = await login(form.email, form.aircraft_id, form.personal_id);
    setLoading(false);

    if (result.success) {
      navigate('/dashboard');
    } else {
      setLoginError(result.message);
    }
  };

  const selectedAircraft = aircraft.find(ac => String(ac.id) === String(form.aircraft_id));
  const filteredAircraft = aircraft.filter(ac => {
    const term = acSearch.trim().toLowerCase();
    if (!term) return true;
    return (
      String(ac.registration || '').toLowerCase().includes(term) ||
      String(ac.ac_type || '').toLowerCase().includes(term)
    );
  });

  const handleAircraftSelect = ac => {
    setForm(f => ({ ...f, aircraft_id: String(ac.id) }));
    setErrors(er => ({ ...er, aircraft_id: '' }));
    setLoginError('');
    setAcSearch('');
    setAcDropOpen(false);
  };

  return (
    <div className="login-page">
      <div className="login-container">
        <div className="login-header">
          <img src={topLogo} alt="VWZZ" className="login-logo" />
        </div>

        <form className="login-form" onSubmit={handleSubmit} noValidate>
          {loginError && (
            <div className="login-error">{loginError}</div>
          )}

          <div className="login-field">
            <label className="login-label">Email</label>
            <input
              name="email"
              type="text"
              className={`form-input ${errors.email ? 'error' : ''}`}
              value={form.email}
              onChange={handleChange}
              autoComplete="username"
            />
            {errors.email && <span className="field-error">{errors.email}</span>}
          </div>

          <div className="login-field">
            <label className="login-label">Aircraft</label>
            <div className={`login-ac-wrap ${errors.aircraft_id ? 'error' : ''}`} ref={acDropRef}>
              <button
                type="button"
                className="login-ac-btn"
                disabled={acLoading}
                onClick={() => setAcDropOpen(v => !v)}
              >
                <span className={`login-ac-reg ${selectedAircraft ? 'has-value' : ''}`}>
                  {acLoading
                    ? 'Loading aircraft...'
                    : selectedAircraft
                      ? `${selectedAircraft.registration} • ${selectedAircraft.flight_hours} FH / ${selectedAircraft.flight_cycles} FC`
                      : '— Select aircraft —'}
                </span>
                <span className={`login-ac-chevron ${acDropOpen ? 'open' : ''}`} aria-hidden="true">▾</span>
              </button>

              <div className={`login-ac-dropdown ${acDropOpen ? 'open' : 'closed'}`}>
                <div className="login-ac-search-wrap">
                  <input
                    ref={acSearchRef}
                    type="text"
                    className="login-ac-search"
                    value={acSearch}
                    onChange={e => setAcSearch(e.target.value)}
                    placeholder="Search registration or type"
                  />
                </div>
                <div className="login-ac-list">
                  {filteredAircraft.map(ac => (
                    <button
                      key={ac.id}
                      type="button"
                      className={`login-ac-option ${String(form.aircraft_id) === String(ac.id) ? 'active' : ''}`}
                      onClick={() => handleAircraftSelect(ac)}
                    >
                      <span className="option-reg">{ac.registration}</span>
                      <span className="option-type">{ac.ac_type}</span>
                    </button>
                  ))}
                  {filteredAircraft.length === 0 ? <div className="login-ac-empty">No matching aircraft</div> : null}
                </div>
              </div>
            </div>
            {errors.aircraft_id && <span className="field-error">{errors.aircraft_id}</span>}
          </div>

          <div className="login-field">
            <label className="login-label">Personal ID (Password)</label>
            <input
              name="personal_id"
              type="password"
              className={`form-input ${errors.personal_id ? 'error' : ''}`}
              value={form.personal_id}
              onChange={handleChange}
              autoComplete="current-password"
            />
            {errors.personal_id && <span className="field-error">{errors.personal_id}</span>}
          </div>

          <button type="submit" className="login-submit" disabled={loading}>
            {loading ? (
              <>
                <div className="spinner" />
                Signing in…
              </>
            ) : (
              <>
                <img src={securityIcon} alt="" className="login-submit-icon app-icon app-icon-security" />
                Sign In
              </>
            )}
          </button>
        </form>
      </div>
    </div>
  );
};

export default LoginPage;


